// 🔧 engagement_tracker.js
// ✅ Purpose: Client gateway for Engagement Intelligence tracking
// ✅ Features:
// - Sends engagement events to secure Cloud Function
// - Also sends matching GA4 events
// - Prevents direct browser writes to engagement_events
// - Used by Passport and future LGV engagement producers
// - Sends only minimal page/activity context; never raw query, referrer, or user-agent data

import { httpsCallable } from "firebase/functions";

import { functions } from "../../firebase";
import { logger } from "../../utils/logger";
import { trackEvent } from "../../utils/analytics";

// ------------------------------------------------------------
// Maintain a lightweight engagement session.
// This lets BI group actions that happened during one visit.
// ------------------------------------------------------------

const SESSION_KEY = "lgv_engagement_session_id";

const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    sessionId =
      `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 10)}`;

    sessionStorage.setItem(
      SESSION_KEY,
      sessionId
    );
  }

  return sessionId;
};

const createRequestId = () => {
  if (typeof window !== "undefined" &&
    typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).substring(2, 12)}`;
};

export const recordEngagement = async ({
  eventType,
  eventId,
  source = "passport",
  metadata = {},
}) => {
  if (!eventType || !eventId) {
    throw new Error("eventType and eventId are required.");
  }

  try {
    const requestId = createRequestId();
    const recordEngagementEvent = httpsCallable(
      functions,
      "recordEngagementEvent"
    );

    // ------------------------------------------------------------
    // Standard client-side metadata envelope. Keep persistent Engagement BI
    // context minimal; GA owns browser/device analytics separately.
    // ------------------------------------------------------------

    const enrichedMetadata = {
      sessionId: getSessionId(),

      page: window.location.pathname,

      ...metadata,
    };

    const result = await recordEngagementEvent({
      eventType,
      eventId,
      requestId,
      source,
      metadata: enrichedMetadata,
    });

    trackEvent({
      action: eventType,
      parameters: {
        eventId,
        engagement_request_id: requestId,
        source,
        ...enrichedMetadata,
      },
    });

    logger.log("Engagement recorded.", {
      eventType,
      eventId,
      source,
    });

    return result?.data || { success: true };
  } catch (err) {
    logger.error("Failed to record engagement.", {
      value: err,
    });

    throw err;
  }
};
